'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';

import IconButton from '../IconButton/IconButton';
import Typography from '../Typography/Typography';
import Box from '../Box/Box';
import Iconography from '../Iconography/Iconography';

import {
  getWeekStart,
  getWeekdayLabels,
  getLocalizedDatePlaceholder,
  getNormalizedDate,
  parseDateInput,
} from './utils';

import styles from './DatePicker.module.scss';

// Type definitions
type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';
type Size = 'small' | 'medium' | 'large';
type Locale =
  | 'en-US'
  | 'en-CA'
  | 'fr-CA'
  | 'de-DE'
  | 'nl-NL'
  | 'de-CH'
  | 'fr-CH'
  | 'nb-NO'
  | 'en-EU'
  | 'en-AE'
  | 'ar-AE'
  | 'en-SA'
  | 'ar-SA'
  | 'fr-BE';

interface ValidationResponse {
  validationType?: string;
  message?: string;
}

interface CalendarDay {
  day: number;
  currentMonth: boolean;
  date: Date;
}

export interface DatePickerProps {
  value?: Date | string | null;
  onChange?: (date: Date) => void;
  locale?: Locale;
  label?: string;
  helperText?: string;
  direction?: Direction;
  theme?: Theme;
  size?: Size;
  required?: boolean;
  name: string;
  register?: (name: string) => Record<string, unknown>;
  selectableDates?: string[];
  /**
   * Minimum selectable date. Dates before this will be disabled.
   * Can be a Date object, ISO string, or 'today' for current date.
   */
  minDate?: Date | string | 'today';
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  /**
   * Whether the date picker is disabled.
   */
  disabled?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  locale = 'en-US',
  label,
  helperText,
  direction = 'ltr',
  theme = 'dark',
  size = 'medium',
  required = false,
  name,
  register,
  selectableDates = [],
  minDate,
  validationResponse,
  showValidation = true,
  disabled = false,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (!value) {
      return null;
    }

    const initialDate = parseDateInput(value);
    return Number.isNaN(initialDate.getTime()) ? null : initialDate;
  });
  const [calendarDate, setCalendarDate] = useState(() => {
    if (!value) {
      return new Date();
    }

    const initialDate = parseDateInput(value);
    return Number.isNaN(initialDate.getTime()) ? new Date() : initialDate;
  });
  const currentDate = new Date();
  const calendarRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Track the last synced value to prevent re-render loops
  const lastSyncedValueRef = useRef<string | null>(null);

  // Compute minimum date for disabling past dates
  const computedMinDate = (() => {
    if (!minDate) return null;
    if (minDate === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    }
    const date = new Date(minDate);
    date.setHours(0, 0, 0, 0);
    return date;
  })();

  const isBeforeMinDate = (date: Date): boolean => {
    if (!computedMinDate) return false;
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < computedMinDate;
  };

  const startDay = getWeekStart(locale);
  const weekdays = getWeekdayLabels(locale, startDay);
  const currentMonth = calendarDate.getMonth();
  const currentYear = calendarDate.getFullYear();

  const formatDate = (date: Date): string => {
    const normalizedDate = new Date(date);
    return normalizedDate.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const normalizedSelectableDates = useMemo(() => {
    if (!Array.isArray(selectableDates)) {
      return [] as string[];
    }

    return selectableDates.filter((dateString): dateString is string => {
      return typeof dateString === 'string' && dateString.trim().length > 0;
    });
  }, [selectableDates]);

  const selectableDatesSet = useMemo(() => {
    const normalizedSet = new Set<string>();

    normalizedSelectableDates.forEach((dateString) => {
      const [year, month, day] = dateString.split('-').map(Number);
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return;
      }

      const utc = new Date(Date.UTC(year, month - 1, day));
      normalizedSet.add(getNormalizedDate(utc));
    });

    return normalizedSet;
  }, [normalizedSelectableDates]);

  const calendarDays = useMemo((): CalendarDay[] => {
    const days: CalendarDay[] = [];
    const startOfMonth = new Date(currentYear, currentMonth, 1);
    const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = endOfMonth.getDate();
    const firstWeekDay = (startOfMonth.getDay() - startDay + 7) % 7;

    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

    for (let i = firstWeekDay; i > 0; i--) {
      const day = daysInPrevMonth - i + 1;
      days.push({
        day,
        currentMonth: false,
        date: new Date(Date.UTC(prevYear, prevMonth, day)),
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        currentMonth: true,
        date: new Date(Date.UTC(currentYear, currentMonth, i)),
      });
    }

    const totalCells = days.length <= 35 ? 35 : 42;
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    const remaining = totalCells - days.length;

    for (let i = 1; i <= remaining; i++) {
      days.push({
        day: i,
        currentMonth: false,
        date: new Date(Date.UTC(nextYear, nextMonth, i)),
      });
    }

    return days;
  }, [currentYear, currentMonth, startDay]);

  const handleSelectDate = useCallback(
    (date: Date) => {
      // Update ref before state to prevent useEffect from re-syncing
      lastSyncedValueRef.current = getNormalizedDate(date);
      setSelectedDate(date);
      setCalendarDate(date);
      setShowCalendar(false);
      onChange?.(date);
    },
    [onChange]
  );

  const handleDocumentClick = (e: MouseEvent) => {
    if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
      setShowCalendar(false);
    }
  };

  useEffect(() => {
    if (calendarRef.current && dropdownRef.current) {
      const width = calendarRef.current.offsetWidth;
      dropdownRef.current.style.minWidth = `${width}px`;
      dropdownRef.current.style.width = 'fit-content';
    }
    if (showCalendar) {
      document.addEventListener('mousedown', handleDocumentClick);
    } else {
      document.removeEventListener('mousedown', handleDocumentClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [showCalendar]);

  useEffect(() => {
    // Handle null/empty value
    if (value === null || value === undefined || value === '') {
      if (lastSyncedValueRef.current === 'null') {
        return; // Already synced to null, skip
      }
      lastSyncedValueRef.current = 'null';
      setSelectedDate((prev) => (prev === null ? prev : null));
      return;
    }

    const nextDate = parseDateInput(value);
    if (Number.isNaN(nextDate.getTime())) {
      return;
    }

    const nextNormalized = getNormalizedDate(nextDate);

    // Skip if we already synced this date value
    if (lastSyncedValueRef.current === nextNormalized) {
      return;
    }

    // Update the ref to track we've synced this value
    lastSyncedValueRef.current = nextNormalized;

    setSelectedDate(nextDate);
    setCalendarDate(nextDate);
  }, [value]);

  const handlePrevMonth = () => {
    setCalendarDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const sizeClassMap = {
    small: styles.sizeSmall,
    medium: styles.sizeMedium,
    large: styles.sizeLarge,
  };

  return (
    <div
      className={classnames(styles.datePicker, styles[theme], sizeClassMap[size], {
        [styles[`validation-${validationResponse?.validationType}`]]:
          validationResponse?.validationType,
      })}
      ref={calendarRef}
    >
      {(label || helperText) && (
        <Box mb={4}>
          {label && (
            <label htmlFor={name}>
              <Typography
                className={styles.label}
                variant="label"
                size="small"
                direction={direction}
                theme={theme}
                color="gray-3"
              >
                {label}
                {required && '*'}
              </Typography>
            </label>
          )}
          {helperText && (
            <Typography
              className={styles.helperText}
              variant="body"
              size="extraSmall"
              direction={direction}
              theme={theme}
              color="gray-5"
            >
              {helperText}
            </Typography>
          )}
        </Box>
      )}
      <Box
        className={classnames(styles.inputContainer, styles[theme], {
          [styles.disabled]: disabled,
        })}
        onClick={() => !disabled && setShowCalendar(!showCalendar)}
      >
        <input
          readOnly
          disabled={disabled}
          id={name}
          name={name}
          type="text"
          placeholder={getLocalizedDatePlaceholder(locale)}
          value={selectedDate ? formatDate(selectedDate) : ''}
          className={classnames(styles.dateInput, styles[theme], {
            [styles.disabled]: disabled,
          })}
          {...(register ? register(name) : {})}
        />
        <Iconography category="global" name="calendar" color="white" theme={theme} />
      </Box>

      {showCalendar && (
        <Box
          className={classnames(styles.calendar, styles[theme])}
          ref={dropdownRef}
          tabIndex={-1}
          theme={theme}
          background="gray-9"
        >
          <div className={styles.calendarHeader}>
            <IconButton
              icon={{ category: 'arrows', name: 'chevron-left' }}
              size="small"
              handleClick={handlePrevMonth}
              theme={theme}
              gaComponent="DatePicker"
              dataTestId="date-picker-prev-month"
            />
            <Typography variant="button" size="medium" color="white" theme={theme}>
              {calendarDate.toLocaleString(locale, { month: 'long' })} {calendarDate.getFullYear()}
            </Typography>
            <IconButton
              icon={{ category: 'arrows', name: 'chevron-right' }}
              size="small"
              handleClick={handleNextMonth}
              theme={theme}
              gaComponent="DatePicker"
              dataTestId="date-picker-next-month"
            />
          </div>

          <Box className={styles.calendarGrid}>
            {weekdays.map((wd: string) => (
              <Typography
                key={wd}
                className={`${styles.calendarCell} ${styles.calendarWeekday}`}
                variant="label"
                size="small"
                color="gray-5"
                theme={theme}
              >
                {wd}
              </Typography>
            ))}

            {calendarDays.map(({ day, currentMonth, date }) => {
              const isSelected = selectedDate
                ? getNormalizedDate(selectedDate) === getNormalizedDate(date)
                : getNormalizedDate(currentDate) === getNormalizedDate(date);
              const isAvailable = selectableDatesSet.has(getNormalizedDate(date));
              const isPastMinDate = isBeforeMinDate(date);
              const allowsSelection = normalizedSelectableDates.length > 0;
              const isDisabled = isPastMinDate || (!isAvailable && allowsSelection);

              return (
                <Typography
                  key={date.toISOString()}
                  className={classnames(styles.calendarCell, styles.day, styles[theme], {
                    [styles.isAvailable]: isAvailable && allowsSelection,
                    [styles.selected]: isSelected && !isDisabled,
                    [styles.notCurrentMonth]: !currentMonth,
                    [styles.disabled]: isDisabled,
                  })}
                  onClick={() => {
                    if (isDisabled) return;
                    if (allowsSelection) {
                      isAvailable && handleSelectDate(date);
                    } else {
                      handleSelectDate(date);
                    }
                  }}
                  variant="body"
                  size="small"
                >
                  {day}
                </Typography>
              );
            })}
          </Box>
        </Box>
      )}

      {showValidation && (
        <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
          {validationResponse?.message && (
            <Typography
              className={classnames(styles.validationMessage)}
              variant="body"
              size="extraSmall"
              direction={direction}
              theme={theme}
              id={`${name}-validation-message`}
            >
              {validationResponse.message || ''}
            </Typography>
          )}
        </Box>
      )}
    </div>
  );
};

// Custom comparison to prevent unnecessary re-renders
const arePropsEqual = (prevProps: DatePickerProps, nextProps: DatePickerProps): boolean => {
  // Compare primitive props
  if (
    prevProps.name !== nextProps.name ||
    prevProps.theme !== nextProps.theme ||
    prevProps.direction !== nextProps.direction ||
    prevProps.size !== nextProps.size ||
    prevProps.label !== nextProps.label ||
    prevProps.helperText !== nextProps.helperText ||
    prevProps.required !== nextProps.required ||
    prevProps.locale !== nextProps.locale ||
    prevProps.showValidation !== nextProps.showValidation ||
    prevProps.disabled !== nextProps.disabled
  ) {
    return false;
  }

  // Compare validation response
  if (
    prevProps.validationResponse?.validationType !== nextProps.validationResponse?.validationType ||
    prevProps.validationResponse?.message !== nextProps.validationResponse?.message
  ) {
    return false;
  }

  // Compare value - normalize both to string for comparison
  const prevValue = prevProps.value;
  const nextValue = nextProps.value;

  if (prevValue === nextValue) {
    return true;
  }

  // Both null/undefined/empty
  if (!prevValue && !nextValue) {
    return true;
  }

  // One is null/undefined/empty, the other is not
  if (!prevValue || !nextValue) {
    return false;
  }

  // Compare normalized date strings
  try {
    const prevNormalized = getNormalizedDate(parseDateInput(prevValue));
    const nextNormalized = getNormalizedDate(parseDateInput(nextValue));
    return prevNormalized === nextNormalized;
  } catch {
    return false;
  }
};

export default React.memo(DatePicker, arePropsEqual);
